import { Injectable, Injector } from "@angular/core";
import { BehaviorSubject, catchError, distinctUntilChanged, from, map, of, shareReplay, startWith, Subject, switchMap, tap } from "rxjs";
// Type-only + lazy: this service is injected eagerly by app.component (for notification$),
// but it must NOT drag OnboardingService / BusinessOnboarding_pb into the eager main.js.
// The proto type is erased at build; OnboardingService is loaded lazily only when a
// scrape is actually triggered (the guest onboard flow — already a lazy route).
import type { UpdateBusinessOnboardingDetailsRequest } from "../grpc-models/BusinessOnboarding_pb";

interface ScrapingState {
    result: any;
    loading: boolean;
}

export interface ScrapingNotification {
    type: 'success' | 'error';
    message: string;
    timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class WebsiteScrapeService {
    
    private scrapeTrigger$ = new Subject<UpdateBusinessOnboardingDetailsRequest>();
    private resultState$ = new BehaviorSubject<ScrapingState>({ result: null, loading: false });
    private notificationSubject$ = new Subject<ScrapingNotification>();

    private state$ = this.scrapeTrigger$.pipe(
        distinctUntilChanged(),
        switchMap((request: UpdateBusinessOnboardingDetailsRequest) => {

            return from(
                // Lazy-load OnboardingService only when a scrape actually runs, so its
                // BusinessOnboarding_pb stays out of the eager bundle.
                import('./onboarding.service').then(m =>
                    this.injector.get(m.OnboardingService).scrapeAndUpdateBusinessOnboardingDetails(request)
                )
            ).pipe(
                tap(result => {
                    if (result && result.issuccess) {
                        this.notificationSubject$.next({
                            type: 'success',
                            message: 'Website data fetched successfully',
                            timestamp: Date.now()
                        });
                    } else {
                        this.notificationSubject$.next({
                            type: 'error',
                            message: 'No data returned from scraping',
                            timestamp: Date.now()
                        });
                    }
                }),
                map(result => ({ result, loading: false })),
                startWith({ result: null, loading: true }),
                catchError((error) => {
                    this.notificationSubject$.next({
                        type: 'error',
                        message: 'Failed to fetch website data',
                        timestamp: Date.now()
                    });
                    return of({ result: null, loading: false });
                })
            );
        }),
        shareReplay(1)
    );


    constructor(private injector: Injector) {
        this.state$.subscribe(state => {
            this.resultState$.next(state);
        });
    }

    scrape(request: UpdateBusinessOnboardingDetailsRequest) {
        this.scrapeTrigger$.next(request);
    }

    resetResult(): void {
        this.resultState$.next({ result: null, loading: false });
    }

    get result$() {
        return this.resultState$.pipe(map(s => s.result));
    }

    get loading$() {
        return this.resultState$.pipe(map(s => s.loading));
    }

    get notification$() {
        return this.notificationSubject$.asObservable();
    }

}
